import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateWikiSectionInput,
  createWikiSectionSchema,
  queryKeys,
  type WikiGuest,
  type WikiPageListItem,
  type WikiSection,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { BookOpen, Globe, Lock, Plus, ShieldAlert, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/**
 * The staff wiki: sections in the left rail, that section's pages in a
 * DataTable on the right, and — for `wiki:manage` — a Guest access tab for
 * inviting outside collaborators to specific sections. Publishing (page or
 * section) happens on the page editor itself (`wiki-page.tsx`), not here.
 */
export function WikiPage() {
  const { can } = useAuth()
  const canManage = can('wiki:manage')

  return (
    <Page>
      <PageHeader
        title="Wiki"
        description="Staff knowledge base. A public section with published pages is readable on the website without signing in."
      />
      <Tabs defaultValue="pages">
        <TabsList>
          <TabsTrigger value="pages">Pages</TabsTrigger>
          {canManage ? <TabsTrigger value="guests">Guest access</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="pages">
          <PagesTab />
        </TabsContent>
        {canManage ? (
          <TabsContent value="guests">
            <GuestsTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </Page>
  )
}

// ------------------------------------------------------------------- pages tab

function PagesTab() {
  const { can } = useAuth()
  const canManage = can('wiki:manage')
  const canWrite = can('wiki:write')
  const navigate = useNavigate()

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [creatingSection, setCreatingSection] = useState(false)
  const [editingSection, setEditingSection] = useState<WikiSection | null>(null)
  const [deletingSection, setDeletingSection] = useState<WikiSection | null>(null)
  const [creatingPage, setCreatingPage] = useState(false)

  const { data: sections, isLoading: sectionsLoading } = useQuery({
    queryKey: queryKeys.wiki.sections(),
    queryFn: () => api.get<WikiSection[]>('/wiki/sections'),
  })

  // Land on the first section once sections load, rather than showing an
  // empty page list before anything is picked.
  useEffect(() => {
    if (!selectedSectionId && sections && sections.length > 0) {
      setSelectedSectionId(sections[0]?.id ?? null)
    }
  }, [sections, selectedSectionId])

  const filters = { sectionId: selectedSectionId ?? undefined, pageSize: 50 }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.wiki.pages(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: WikiPageListItem[]; total: number; page: number; pageSize: number }>(
        '/wiki/pages',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled: Boolean(selectedSectionId),
  })
  const pages = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns: ColumnDef<WikiPageListItem, unknown>[] = [
    {
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5 font-medium">
          {row.original.restricted ? (
            <ShieldAlert
              className="size-3.5 shrink-0 text-foreground-muted"
              aria-label="Restricted"
            />
          ) : null}
          {row.original.title}
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'isPublished',
      cell: ({ row }) =>
        row.original.isPublished ? (
          <Badge tone="success">Published</Badge>
        ) : (
          <Badge tone="neutral">Draft</Badge>
        ),
    },
    {
      header: 'Updated',
      accessorKey: 'updatedAt',
      cell: ({ row }) => (
        <span className="text-xs text-foreground-muted">
          {row.original.updatedByName ? `${row.original.updatedByName} · ` : ''}
          {new Date(row.original.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
    },
  ]

  const selectedSection = sections?.find((s) => s.id === selectedSectionId) ?? null

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
      <div className="rounded-lg border border-border bg-surface p-2">
        <div className="flex items-center justify-between px-2 py-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
            Sections
          </p>
          {canManage ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="New section"
              onClick={() => setCreatingSection(true)}
            >
              <Plus className="size-3.5" />
            </Button>
          ) : null}
        </div>
        {sectionsLoading ? null : sections && sections.length > 0 ? (
          <ul className="space-y-0.5">
            {sections.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => setSelectedSectionId(section.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors',
                    section.id === selectedSectionId
                      ? 'bg-sidebar-active text-sidebar-active-foreground'
                      : 'text-foreground hover:bg-surface-sunken',
                  )}
                >
                  {section.visibility === 'public' ? (
                    <Globe className="size-3.5 shrink-0 text-foreground-muted" aria-hidden="true" />
                  ) : (
                    <Lock className="size-3.5 shrink-0 text-foreground-muted" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{section.name}</span>
                  <span className="text-2xs tabular text-foreground-muted">
                    {section.pageCount}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-4 text-center text-xs text-foreground-muted">
            {canManage ? 'Create a section to start writing.' : 'No sections yet.'}
          </p>
        )}
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {selectedSection ? (
              <>
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-sm font-semibold">{selectedSection.name}</h2>
                  <Badge tone={selectedSection.visibility === 'public' ? 'success' : 'neutral'}>
                    {selectedSection.visibility === 'public' ? 'Public' : 'Internal'}
                  </Badge>
                </div>
                {selectedSection.description ? (
                  <p className="text-xs text-foreground-muted">{selectedSection.description}</p>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="flex shrink-0 gap-2">
            {selectedSection && canManage ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditingSection(selectedSection)}
                >
                  Edit section
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeletingSection(selectedSection)}
                >
                  Delete
                </Button>
              </>
            ) : null}
            {selectedSection && canWrite ? (
              <Button variant="primary" size="sm" onClick={() => setCreatingPage(true)}>
                <Plus /> New page
              </Button>
            ) : null}
          </div>
        </div>

        <DataTable
          data={pages}
          columns={columns}
          isLoading={isLoading}
          onRowClick={(row) => void navigate({ to: '/wiki/$pageId', params: { pageId: row.id } })}
          infinite={{
            total,
            hasMore: Boolean(hasNextPage),
            isFetchingMore: isFetchingNextPage,
            onLoadMore: () => void fetchNextPage(),
          }}
          empty={
            selectedSection ? (
              <EmptyState
                icon={BookOpen}
                title="No pages yet"
                description="Add the first page in this section."
                action={
                  canWrite ? { label: 'New page', onClick: () => setCreatingPage(true) } : undefined
                }
              />
            ) : (
              <EmptyState
                icon={BookOpen}
                title="No section selected"
                description="Choose a section on the left, or create one."
              />
            )
          }
        />
      </div>

      <CreateSectionDialog
        open={creatingSection}
        onOpenChange={setCreatingSection}
        onCreated={setSelectedSectionId}
      />
      <EditSectionDialog section={editingSection} onClose={() => setEditingSection(null)} />
      <DeleteSectionDialog
        section={deletingSection}
        onClose={() => setDeletingSection(null)}
        onDeleted={() => setSelectedSectionId(null)}
      />
      <CreatePageDialog
        open={creatingPage}
        onOpenChange={setCreatingPage}
        sectionId={selectedSectionId}
        sections={sections ?? []}
      />
    </div>
  )
}

type SectionFormValues = z.input<typeof createWikiSectionSchema>

function CreateSectionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SectionFormValues, unknown, CreateWikiSectionInput>({
    resolver: zodResolver(createWikiSectionSchema),
    defaultValues: { visibility: 'internal' },
  })

  useEffect(() => {
    if (!open) reset({ visibility: 'internal' })
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    try {
      const section = await api.post<WikiSection>('/wiki/sections', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.sections() })
      toast.success('Section created')
      onOpenChange(false)
      onCreated(section.id)
    } catch (error) {
      toast.error(
        'Could not create the section',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New section</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>
            <Field
              label="Description"
              error={errors.description?.message}
              hint="Shown above the page list, here and on the public page if this section is public."
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={2}
                  {...register('description', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                />
              )}
            </Field>
            <div className="flex items-start gap-3">
              <Switch
                id="section-public"
                checked={watch('visibility') === 'public'}
                onCheckedChange={(checked) =>
                  setValue('visibility', checked ? 'public' : 'internal')
                }
              />
              <label className="text-sm" htmlFor="section-public">
                Public
                <span className="block text-xs text-foreground-muted">
                  Readable on the website without signing in, once at least one page here is
                  published.
                </span>
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create section
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditSectionDialog({
  section,
  onClose,
}: {
  section: WikiSection | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<'internal' | 'public'>('internal')

  useEffect(() => {
    if (!section) return
    setName(section.name)
    setDescription(section.description ?? '')
    setVisibility(section.visibility)
  }, [section])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/wiki/sections/${section?.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        visibility,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.sections() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={section !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {section?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Description">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-start gap-3">
            <Switch
              id="edit-section-public"
              checked={visibility === 'public'}
              onCheckedChange={(checked) => setVisibility(checked ? 'public' : 'internal')}
            />
            <label className="text-sm" htmlFor="edit-section-public">
              Public
              <span className="block text-xs text-foreground-muted">
                {visibility === 'public'
                  ? 'Published pages here are live on the website.'
                  : 'Switching this off instantly hides every page here from the website, even published ones.'}
              </span>
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteSectionDialog({
  section,
  onClose,
  onDeleted,
}: {
  section: WikiSection | null
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.delete(`/wiki/sections/${section?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.sections() })
      toast.success('Section deleted')
      onDeleted()
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  const occupied = (section?.pageCount ?? 0) > 0

  return (
    <Dialog open={section !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {section?.name}?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm">
            {occupied
              ? `Move or delete its ${section?.pageCount} ${section?.pageCount === 1 ? 'page' : 'pages'} first.`
              : 'This section has no pages, so nothing else is affected.'}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending || occupied}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreatePageDialog({
  open,
  onOpenChange,
  sectionId,
  sections,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sectionId: string | null
  sections: WikiSection[]
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [targetSectionId, setTargetSectionId] = useState('')

  useEffect(() => {
    if (open) setTargetSectionId(sectionId ?? '')
  }, [open, sectionId])

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/wiki/pages', {
        sectionId: targetSectionId,
        title,
        contentHtml: '',
      }),
    onSuccess: async (page) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.all() })
      onOpenChange(false)
      setTitle('')
      await navigate({ to: '/wiki/$pageId', params: { pageId: page.id } })
    },
    onError: (error: Error) =>
      toast.error(
        'Could not create the page',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New page</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Section" required>
            {(props) => (
              <Select value={targetSectionId} onValueChange={setTargetSectionId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a section" />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Title" required>
            {(props) => (
              <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || !title.trim() || !targetSectionId}
          >
            {create.isPending ? 'Creating…' : 'Create page'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------------ guests tab

function SectionCheckboxList({
  sections,
  selected,
  onToggle,
}: {
  sections: WikiSection[]
  selected: Set<string>
  onToggle: (id: string) => void
}) {
  if (sections.length === 0) {
    return (
      <p className="text-xs text-foreground-muted">No sections exist yet — create one first.</p>
    )
  }
  return (
    <ul className="max-h-48 space-y-1 overflow-y-auto scrollbar-thin rounded-md border border-border p-2">
      {sections.map((section) => (
        <li key={section.id}>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-3.5 accent-[var(--primary)]"
              checked={selected.has(section.id)}
              onChange={() => onToggle(section.id)}
            />
            {section.name}
          </label>
        </li>
      ))}
    </ul>
  )
}

function toggleInSet(set: Set<string>, id: string): Set<string> {
  const next = new Set(set)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

function GuestsTab() {
  const toast = useToast()
  const [inviting, setInviting] = useState(false)
  const [editingGuest, setEditingGuest] = useState<WikiGuest | null>(null)

  const { data: sections } = useQuery({
    queryKey: queryKeys.wiki.sections(),
    queryFn: () => api.get<WikiSection[]>('/wiki/sections'),
  })
  const { data: guests, isLoading } = useQuery({
    queryKey: queryKeys.wiki.guests(),
    queryFn: () => api.get<WikiGuest[]>('/wiki/guests'),
  })

  const sectionName = (id: string) => sections?.find((s) => s.id === id)?.name ?? 'Unknown section'

  const resend = useMutation({
    mutationFn: (id: string) => api.post(`/wiki/guests/${id}/invite`, {}),
    onSuccess: () => toast.success('Invite email resent'),
    onError: (error: Error) =>
      toast.error(
        'Could not resend the invite',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const columns: ColumnDef<WikiGuest, unknown>[] = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }) => <span className="text-foreground-muted">{row.original.email}</span>,
    },
    {
      header: 'Sections',
      id: 'sections',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.sectionIds.length === 0 ? (
            <span className="text-foreground-subtle">None granted</span>
          ) : (
            row.original.sectionIds.map((id) => (
              <Badge key={id} tone="neutral">
                {sectionName(id)}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'isActive',
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Deactivated</Badge>
        ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditingGuest(row.original)}>
            Sections
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resend.mutate(row.original.id)}
            disabled={resend.isPending}
          >
            Resend invite
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-foreground-muted">
          Guests sign in the same way as staff, but see only the Wiki — and only the sections
          granted here. Deactivate a guest from the Staff page, the same as any other account.
        </p>
        <Button variant="primary" size="sm" onClick={() => setInviting(true)}>
          <UserPlus /> Invite guest
        </Button>
      </div>
      <DataTable
        data={guests ?? []}
        columns={columns}
        isLoading={isLoading}
        empty={
          <EmptyState
            icon={UserPlus}
            title="No guests invited"
            description="Invite an outside collaborator to specific sections of the wiki."
            action={{ label: 'Invite guest', onClick: () => setInviting(true) }}
          />
        }
      />
      <InviteGuestDialog open={inviting} onOpenChange={setInviting} sections={sections ?? []} />
      <EditGuestSectionsDialog
        guest={editingGuest}
        sections={sections ?? []}
        onClose={() => setEditingGuest(null)}
      />
    </div>
  )
}

function InviteGuestDialog({
  open,
  onOpenChange,
  sections,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sections: WikiSection[]
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [sectionIds, setSectionIds] = useState<Set<string>>(new Set())

  const reset = () => {
    setEmail('')
    setName('')
    setSectionIds(new Set())
  }

  const invite = useMutation({
    mutationFn: () =>
      api.post<WikiGuest>('/wiki/guests', { email, name, sectionIds: [...sectionIds] }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.guests() })
      toast.success('Guest invited — an email was sent with a link to set their password')
      onOpenChange(false)
      reset()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not invite the guest',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a guest</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Email" required>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Sections they can see" hint="They'll see only these, never the full wiki.">
            {() => (
              <SectionCheckboxList
                sections={sections}
                selected={sectionIds}
                onToggle={(id) => setSectionIds((prev) => toggleInSet(prev, id))}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => invite.mutate()}
            disabled={invite.isPending || !email.trim() || !name.trim()}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditGuestSectionsDialog({
  guest,
  sections,
  onClose,
}: {
  guest: WikiGuest | null
  sections: WikiSection[]
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [sectionIds, setSectionIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setSectionIds(new Set(guest?.sectionIds ?? []))
  }, [guest])

  const save = useMutation({
    mutationFn: () =>
      api.put(`/wiki/guests/${guest?.id}/sections`, { sectionIds: [...sectionIds] }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.guests() })
      toast.success('Sections updated')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={guest !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sections for {guest?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <SectionCheckboxList
            sections={sections}
            selected={sectionIds}
            onToggle={(id) => setSectionIds((prev) => toggleInSet(prev, id))}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
